import React from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Grid,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  TableContainer,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useColorModeValue,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  CalendarIcon,
  ChevronDownIcon,
  DeleteIcon,
  EditIcon,
  ViewIcon,
} from '@chakra-ui/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Calendar from 'react-calendar';
import EmptyState from 'components/ui/EmptyState';
import SectionHeader from 'components/ui/SectionHeader';
import Surface from 'components/ui/Surface';
import useVehicleInspections, {
  inspectionKeys,
} from 'hooks/useVehicleInspections';
import inspectionApi from 'services/inspection.api';

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : '-';

const formatCalendarValue = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatDateFieldLabel = (value) => {
  if (!value) {
    return 'dd/mm/yyyy';
  }

  const date = new Date(value);
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const monthOptions = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Feb' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' },
  { value: 5, label: 'May' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dec' },
];

function FilterDatePicker({ label, value, onChange }) {
  const labelColor = useColorModeValue(
    'secondaryGray.700',
    'secondaryGray.500',
  );
  const fieldText = useColorModeValue('secondaryGray.900', 'white');
  const placeholderText = useColorModeValue(
    'secondaryGray.600',
    'secondaryGray.500',
  );
  const fieldBg = useColorModeValue(
    'rgba(255,255,255,0.64)',
    'rgba(255,255,255,0.05)',
  );
  const fieldBorder = useColorModeValue(
    'rgba(162, 176, 210, 0.28)',
    'rgba(255,255,255,0.08)',
  );
  const fieldHover = useColorModeValue(
    'rgba(111, 66, 193, 0.22)',
    'rgba(174, 134, 242, 0.28)',
  );
  const focusShadow = useColorModeValue(
    '0 0 0 4px rgba(111, 66, 193, 0.10)',
    '0 0 0 4px rgba(174, 134, 242, 0.12)',
  );
  const popoverBg = useColorModeValue(
    'rgba(255,255,255,0.98)',
    'rgba(17, 23, 39, 0.98)',
  );
  const popoverBorder = useColorModeValue(
    'rgba(162,176,210,0.18)',
    'rgba(255,255,255,0.08)',
  );
  const calendarText = useColorModeValue('#1a202c', '#ffffff');
  const calendarMuted = useColorModeValue('#64748b', '#a0aec0');
  const calendarTileHover = useColorModeValue(
    'rgba(111, 66, 193, 0.10)',
    'rgba(174, 134, 242, 0.18)',
  );
  const calendarActive = useColorModeValue('#6F42C1', '#8f5ae9');
  const buttonLabel = formatDateFieldLabel(value);

  return (
    <Box>
      <Text color={labelColor} fontSize="sm" fontWeight="700" mb="8px">
        {label}
      </Text>
      <Popover placement="bottom-start" gutter={10} strategy="fixed" isLazy>
        <PopoverTrigger>
          <Button
            variant="unstyled"
            w="100%"
            h="50px"
            px="20px"
            borderRadius="18px"
            bg={fieldBg}
            border="1px solid"
            borderColor={fieldBorder}
            _hover={{ borderColor: fieldHover }}
            _focusVisible={{
              borderColor: 'brand.400',
              boxShadow: focusShadow,
            }}
          >
            <HStack justify="space-between" w="100%">
              <Text
                color={value ? fieldText : placeholderText}
                fontSize="md"
                fontWeight="500"
              >
                {buttonLabel}
              </Text>
              <CalendarIcon color={fieldText} boxSize="20px" />
            </HStack>
          </Button>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            w="320px"
            bg={popoverBg}
            border="1px solid"
            borderColor={popoverBorder}
            borderRadius="24px"
            boxShadow="0 24px 64px rgba(15, 23, 42, 0.24)"
            p="16px"
            zIndex={1600}
          >
            <PopoverBody p="0">
              <Calendar
                value={value ? new Date(value) : null}
                onChange={(selectedDate) =>
                  onChange(formatCalendarValue(selectedDate))
                }
                prev2Label={null}
                next2Label={null}
                calendarType="gregory"
                formatShortWeekday={(locale, date) =>
                  date.toLocaleDateString(locale, { weekday: 'narrow' })
                }
                tileClassName="vin-admin-calendar-tile"
              />
              <HStack justify="space-between" mt="12px">
                <Button size="sm" variant="ghost" onClick={() => onChange('')}>
                  Clear
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onChange(formatCalendarValue(new Date()))}
                >
                  Today
                </Button>
              </HStack>
            </PopoverBody>
            <style>{`
              .react-calendar {
                width: 100%;
                border: none;
                background: transparent;
                color: ${calendarText};
                font-family: "Urbanist", sans-serif;
              }
              .react-calendar__navigation button {
                min-width: 40px;
                background: transparent;
                color: ${calendarText};
                border-radius: 12px;
                font-weight: 700;
              }
              .react-calendar__navigation button:enabled:hover,
              .react-calendar__navigation button:enabled:focus {
                background: ${calendarTileHover};
              }
              .react-calendar__month-view__weekdays {
                display: grid;
                grid-template-columns: repeat(7, minmax(0, 1fr));
                padding: 0;
                margin-bottom: 8px;
                text-transform: uppercase;
                font-size: 11px;
                color: ${calendarMuted};
              }
              .react-calendar__month-view__weekdays__weekday {
                padding: 0;
                text-align: center;
              }
              .react-calendar__month-view__weekdays__weekday abbr {
                text-decoration: none;
              }
              .react-calendar__month-view__days {
                display: grid !important;
                grid-template-columns: repeat(7, minmax(0, 1fr));
                gap: 0;
              }
              .react-calendar__month-view__days__day {
                max-width: 100%;
                padding: 0;
              }
              .react-calendar__tile {
                border-radius: 12px;
                color: ${calendarText};
                background: transparent;
                height: 42px;
              }
              .react-calendar__tile:enabled:hover,
              .react-calendar__tile:enabled:focus {
                background: ${calendarTileHover};
              }
              .react-calendar__tile--now {
                background: ${calendarTileHover};
              }
              .react-calendar__tile--active {
                background: ${calendarActive} !important;
                color: #ffffff;
              }
              .react-calendar__month-view__days__day--neighboringMonth {
                color: ${calendarMuted};
              }
            `}</style>
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  );
}

export default function InspectionListView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const [inspectionToDelete, setInspectionToDelete] = React.useState(null);
  const mutedText = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
  const labelText = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
  const tableBorder = useColorModeValue(
    'rgba(162, 176, 210, 0.18)',
    'rgba(255,255,255,0.08)',
  );
  const tableRowHover = useColorModeValue(
    'rgba(111,66,193,0.04)',
    'rgba(111,66,193,0.08)',
  );
  const dialogBg = useColorModeValue(
    'rgba(255,255,255,0.98)',
    'rgba(17, 23, 39, 0.98)',
  );
  const dialogBorder = useColorModeValue(
    'rgba(162,176,210,0.18)',
    'rgba(255,255,255,0.08)',
  );
  const filters = React.useMemo(
    () => ({
      page: Number(searchParams.get('page') || 1),
      limit: Number(searchParams.get('limit') || 10),
      search: searchParams.get('search') || '',
      month: searchParams.get('month') || '',
      year: searchParams.get('year') || '',
      startDate: searchParams.get('startDate') || '',
      endDate: searchParams.get('endDate') || '',
    }),
    [searchParams],
  );

  const [localFilters, setLocalFilters] = React.useState(filters);
  React.useEffect(() => setLocalFilters(filters), [filters]);

  const listQuery = useVehicleInspections(filters);
  const deleteMutation = useMutation({
    mutationFn: inspectionApi.remove,
    onSuccess: () => {
      setInspectionToDelete(null);
      queryClient.invalidateQueries({ queryKey: inspectionKeys.all });
      toast({
        title: 'Inspection deleted',
        status: 'success',
        borderRadius: '18px',
      });
    },
  });

  const applyFilters = () => {
    const next = new URLSearchParams();
    Object.entries({ ...localFilters, page: 1 }).forEach(([key, value]) => {
      if (value) {
        next.set(key, value);
      }
    });
    setSearchParams(next);
  };

  const clearFilters = () => {
    const nextFilters = {
      page: 1,
      limit: filters.limit || 10,
      search: '',
      month: '',
      year: '',
      startDate: '',
      endDate: '',
    };
    setLocalFilters(nextFilters);
    const next = new URLSearchParams();
    if (nextFilters.limit) {
      next.set('limit', String(nextFilters.limit));
    }
    setSearchParams(next);
  };

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.month ||
    filters.year ||
    filters.startDate ||
    filters.endDate,
  );

  const openDeleteConfirm = (inspection) => {
    setInspectionToDelete(inspection);
    onOpen();
  };

  const handleDeleteConfirm = () => {
    if (!inspectionToDelete?.uuid) {
      return;
    }
    deleteMutation.mutate(inspectionToDelete.uuid, {
      onSettled: () => {
        onClose();
      },
    });
  };

  const handleDeleteCancel = () => {
    setInspectionToDelete(null);
    onClose();
  };

  const pagination = listQuery.data?.pagination;
  const rows = listQuery.data?.data || [];

  return (
    <Box pt={{ base: '130px', md: '120px', sm: '120px' }}>
      <Stack spacing="10px" mb="28px">
        <Text
          textTransform="uppercase"
          letterSpacing="0.18em"
          fontSize="xs"
          color={mutedText}
          fontWeight="700"
        >
          Inspection Operations
        </Text>
        <Heading size="2xl">Inspection List</Heading>
        <Text color={mutedText} fontSize="lg" maxW="760px">
          Search by VIN or inspection number, narrow by time, and review records
          with cleaner actions and clearer data hierarchy.
        </Text>
      </Stack>

      <Surface p={{ base: '22px', xl: '28px' }} mb="20px">
        <SectionHeader
          title="Filters"
          description="Adjust search, date range, month, and year before applying the view."
          action={
            <Button
              as={RouterLink}
              to="/admin/inspections/create"
              variant="brand"
              w={{ base: '100%', md: 'auto' }}
            >
              Create Inspection
            </Button>
          }
        />
        <Grid
          templateColumns={{
            base: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            xl: '1.45fr 1.1fr 1.1fr 0.9fr 0.9fr',
          }}
          gap={{ base: '14px', md: '16px', xl: '18px' }}
          sx={{
            marginTop: 10,
          }}
          alignItems="end"
        >
          <Box gridColumn={{ base: 'auto', md: '1 / -1', xl: 'auto' }}>
            <Text
              color={labelText}
              opacity={{ base: 1, xl: 0 }}
              fontSize="sm"
              fontWeight="700"
              mb="8px"
              userSelect="none"
            >
              Search
            </Text>
            <Input
              value={localFilters.search}
              onChange={(event) =>
                setLocalFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              placeholder="Search VIN or inspection number"
              variant="main"
            />
          </Box>
          <Box>
            <FilterDatePicker
              label="Start Date"
              value={localFilters.startDate}
              onChange={(nextValue) =>
                setLocalFilters((current) => ({
                  ...current,
                  startDate: nextValue,
                }))
              }
            />
          </Box>
          <Box>
            <FilterDatePicker
              label="End Date"
              value={localFilters.endDate}
              onChange={(nextValue) =>
                setLocalFilters((current) => ({
                  ...current,
                  endDate: nextValue,
                }))
              }
            />
          </Box>
          <Box>
            <Text color={labelText} fontSize="sm" fontWeight="700" mb="8px">
              Month
            </Text>
            <Select
              value={localFilters.month}
              variant="main"
              h="50px"
              fontSize="md"
              color={localFilters.month ? 'white' : undefined}
              onChange={(event) =>
                setLocalFilters((current) => ({
                  ...current,
                  month: event.target.value,
                }))
              }
            >
              <option value="">All months</option>
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </Select>
          </Box>
          <Box>
            <Text color={labelText} fontSize="sm" fontWeight="700" mb="8px">
              Year
            </Text>
            <Select
              value={localFilters.year}
              variant="main"
              h="50px"
              fontSize="md"
              color={localFilters.year ? 'white' : undefined}
              onChange={(event) =>
                setLocalFilters((current) => ({
                  ...current,
                  year: event.target.value,
                }))
              }
            >
              <option value="">All years</option>
              {[2024, 2025, 2026, 2027].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </Box>
        </Grid>
        <Stack
          mt="16px"
          direction={{ base: 'column', xl: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', xl: 'center' }}
          spacing={{ base: '14px', md: '16px' }}
        >
          <Text color={mutedText} fontSize="sm" maxW="720px">
            Tip: combine VIN search with month, year, or a date range to narrow
            the list faster.
          </Text>
          <Stack
            direction={{ base: 'column', sm: 'row' }}
            spacing="12px"
            w={{ base: '100%', xl: 'auto' }}
            align={{ base: 'stretch', sm: 'center' }}
            justify={{ base: 'stretch', xl: 'flex-end' }}
          >
            <Button
              variant="subtle"
              onClick={clearFilters}
              isDisabled={!hasActiveFilters}
              order={{ base: 2, sm: 1 }}
              w={{ base: '100%', sm: 'auto' }}
            >
              Clear Filters
            </Button>
            <Button
              variant="brand"
              onClick={applyFilters}
              order={{ base: 1, sm: 2 }}
              w={{ base: '100%', sm: 'auto' }}
            >
              Apply Filters
            </Button>
          </Stack>
        </Stack>
      </Surface>

      <Surface p={{ base: '22px', xl: '28px' }}>
        <SectionHeader
          title="Inspection Records"
          description="Review every inspection, then open, edit, or remove records from the actions column."
        />
        <Skeleton isLoaded={!listQuery.isLoading} borderRadius="24px">
          {rows.length === 0 ? (
            <EmptyState
              title="No matching inspections"
              description="Try changing the VIN number, inspection number, or date filter."
            />
          ) : (
            <>
              <Stack spacing="14px" display={{ base: 'flex', lg: 'none' }}>
                {rows.map((inspection) => (
                  <Surface key={inspection.uuid} variant="muted" p="18px">
                    <Stack spacing="14px">
                      <HStack
                        justify="space-between"
                        align="flex-start"
                        spacing="14px"
                      >
                        <Box minW="0">
                          <Text fontWeight="800" fontSize="lg" noOfLines={1}>
                            {inspection.inspectionNumber}
                          </Text>
                          <Text color={mutedText} mt="4px" noOfLines={1}>
                            {inspection.vinNumber}
                          </Text>
                        </Box>

                        <Box
                          display={{
                            base: 'none',
                            sm: 'none',
                            md: 'block',
                          }}
                          minW="130px"
                        >
                          <Text
                            color={labelText}
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="0.08em"
                          >
                            Total Images
                          </Text>
                          <Text mt="4px" fontWeight="700" noOfLines={1}>
                            {inspection.images?.length || 0}
                          </Text>
                        </Box>
                        <Box
                          display={{
                            base: 'none',
                            sm: 'none',
                            md: 'block',
                          }}
                          minW="170px"
                        >
                          <Text
                            color={labelText}
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="0.08em"
                          >
                            Created Date
                          </Text>
                          <Text mt="4px" fontWeight="700" noOfLines={1}>
                            {formatDate(inspection.createdAt)}
                          </Text>
                        </Box>
                        <HStack
                          spacing="8px"
                          flexShrink="0"
                          display={{
                            base: 'none',
                            md: 'flex',
                          }}
                        >
                          <IconButton
                            aria-label="View inspection"
                            icon={<ViewIcon />}
                            size="sm"
                            variant="subtle"
                            onClick={() =>
                              navigate(`/admin/inspections/${inspection.uuid}`)
                            }
                          />
                          <IconButton
                            aria-label="Edit inspection"
                            icon={<EditIcon />}
                            size="sm"
                            variant="subtle"
                            onClick={() =>
                              navigate(
                                `/admin/inspections/${inspection.uuid}/edit`,
                              )
                            }
                          />
                          <IconButton
                            aria-label="Delete inspection"
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="subtle"
                            isLoading={
                              deleteMutation.isPending &&
                              inspectionToDelete?.uuid === inspection.uuid
                            }
                            onClick={() => openDeleteConfirm(inspection)}
                          />
                        </HStack>
                        <Box
                          display={{
                            base: 'none',
                            sm: 'flex',
                            md: 'none',
                          }}
                        >
                          <Menu>
                            <MenuButton as={Button} variant="ghost">
                              <ChevronDownIcon />
                            </MenuButton>
                            <MenuList>
                              <MenuItem
                                onClick={() =>
                                  navigate(
                                    `/admin/inspections/${inspection.uuid}`,
                                  )
                                }
                                icon={<Icon as={ViewIcon} boxSize="16px" />}
                              >
                                View inspection
                              </MenuItem>
                              <MenuItem
                                onClick={() =>
                                  navigate(
                                    `/admin/inspections/${inspection.uuid}/edit`,
                                  )
                                }
                                icon={<Icon as={EditIcon} boxSize="16px" />}
                              >
                                Edit inspection
                              </MenuItem>

                              <MenuItem
                                onClick={() => openDeleteConfirm(inspection)}
                                icon={<Icon as={DeleteIcon} boxSize="16px" />}
                              >
                                Delete inspection
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Box>
                      </HStack>
                      <HStack justify={'space-between'}>
                        <Box
                          display={{
                            base: 'none',
                            sm: 'block',
                            md: 'none',
                          }}
                        >
                          <Text
                            color={labelText}
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="0.08em"
                          >
                            Total Images
                          </Text>
                          <Text mt="4px" fontWeight="700">
                            {inspection.images?.length || 0}
                          </Text>
                        </Box>
                        <Box
                          display={{
                            base: 'none',
                            sm: 'block',
                            md: 'none',
                          }}
                        >
                          <Text
                            color={labelText}
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="0.08em"
                          >
                            Created Date
                          </Text>
                          <Text mt="4px" fontWeight="700">
                            {formatDate(inspection.createdAt)}
                          </Text>
                        </Box>
                      </HStack>
                      {inspection?.remarks ? (
                        <Box>
                          <Text
                            color={labelText}
                            fontSize="xs"
                            textTransform="uppercase"
                            letterSpacing="0.08em"
                          >
                            Remarks
                          </Text>
                          <Text mt="6px" color={mutedText} noOfLines={2}>
                            {inspection.remarks || '-'}
                          </Text>
                        </Box>
                      ) : (
                        <></>
                      )}
                    </Stack>
                  </Surface>
                ))}
              </Stack>

              <TableContainer
                display={{ base: 'none', lg: 'block' }}
                overflowX="auto"
              >
                <Table
                  variant="simple"
                  sx={{
                    tableLayout: 'fixed',
                    borderCollapse: 'collapse',
                    minW: '1120px',
                  }}
                >
                  <Thead>
                    <Tr>
                      <Th w="22%">Inspection Number</Th>
                      <Th w="15%">VIN Number</Th>
                      <Th w="16%">Total Images</Th>
                      <Th w="18%">Remarks</Th>
                      <Th w="17%">Created Date</Th>
                      <Th w="12%" textAlign="right">
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((inspection) => (
                      <Tr
                        key={inspection.uuid}
                        borderBottom="1px solid"
                        borderColor={tableBorder}
                        _hover={{ bg: tableRowHover }}
                      >
                        <Td
                          borderBottom="none"
                          py="24px"
                          fontWeight="800"
                          maxW="0"
                        >
                          <Text noOfLines={1}>
                            {inspection.inspectionNumber}
                          </Text>
                        </Td>
                        <Td borderBottom="none" py="24px" maxW="0">
                          <Text noOfLines={1}>{inspection.vinNumber}</Text>
                        </Td>
                        <Td borderBottom="none" py="24px">
                          {inspection.images?.length || 0}
                        </Td>
                        <Td borderBottom="none" py="24px" maxW="0">
                          <Text noOfLines={1}>{inspection.remarks || '-'}</Text>
                        </Td>
                        <Td borderBottom="none" py="24px" maxW="0">
                          <Text noOfLines={1}>{formatDate(inspection.createdAt)}</Text>
                        </Td>
                        <Td borderBottom="none" py="24px">
                          <HStack spacing="8px" justify="flex-end">
                            <IconButton
                              aria-label="View inspection"
                              icon={<ViewIcon />}
                              size="sm"
                              variant="subtle"
                              onClick={() =>
                                navigate(
                                  `/admin/inspections/${inspection.uuid}`,
                                )
                              }
                            />
                            <IconButton
                              aria-label="Edit inspection"
                              icon={<EditIcon />}
                              size="sm"
                              variant="subtle"
                              onClick={() =>
                                navigate(
                                  `/admin/inspections/${inspection.uuid}/edit`,
                                )
                              }
                            />
                            <IconButton
                              aria-label="Delete inspection"
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              variant="subtle"
                              isLoading={
                                deleteMutation.isPending &&
                                inspectionToDelete?.uuid === inspection.uuid
                              }
                              onClick={() => openDeleteConfirm(inspection)}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </>
          )}
        </Skeleton>

        <Stack
          direction={{ base: 'column', sm: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', sm: 'center' }}
          mt="22px"
          spacing="12px"
        >
          <Text color={mutedText}>
            Page {pagination?.page || 1} of {pagination?.totalPages || 1}
          </Text>
          <HStack w={{ base: '100%', sm: 'auto' }} spacing="10px">
            <Button
              variant="subtle"
              flex={{ base: 1, sm: 'unset' }}
              isDisabled={(pagination?.page || 1) <= 1}
              onClick={() =>
                setSearchParams((current) => {
                  const next = new URLSearchParams(current);
                  next.set('page', String((pagination?.page || 1) - 1));
                  return next;
                })
              }
            >
              Previous
            </Button>
            <Button
              variant="brand"
              flex={{ base: 1, sm: 'unset' }}
              isDisabled={
                (pagination?.page || 1) >= (pagination?.totalPages || 1)
              }
              onClick={() =>
                setSearchParams((current) => {
                  const next = new URLSearchParams(current);
                  next.set('page', String((pagination?.page || 1) + 1));
                  return next;
                })
              }
            >
              Next
            </Button>
          </HStack>
        </Stack>
      </Surface>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleDeleteCancel}
        isCentered
      >
        <AlertDialogOverlay
          bg="rgba(15, 23, 42, 0.48)"
          backdropFilter="blur(6px)"
        >
          <AlertDialogContent
            bg={dialogBg}
            borderRadius="28px"
            border="1px solid"
            borderColor={dialogBorder}
            mx="20px"
          >
            <AlertDialogHeader fontSize="xl" fontWeight="800">
              Confirm delete
            </AlertDialogHeader>

            <AlertDialogBody color={mutedText}>
              Delete{' '}
              <Text as="span" fontWeight="800" color="inherit">
                {inspectionToDelete?.inspectionNumber || 'this inspection'}
              </Text>
              ? This action will remove the inspection record and cannot be
              undone.
            </AlertDialogBody>

            <AlertDialogFooter gap="12px">
              <Button
                ref={cancelRef}
                onClick={handleDeleteCancel}
                variant="subtle"
              >
                Cancel
              </Button>
              <Button
                variant="brand"
                colorScheme="red"
                onClick={handleDeleteConfirm}
                isLoading={deleteMutation.isPending}
              >
                Yes, delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
